/**
 * Shared TanStack Query client with disk persistence.
 *
 * WHY: on a phone, opening the app from a cold start used to flash spinners
 * for wallet balance, rates, contacts, recents - all data we already had
 * from the last session. Persisting the cache to IndexedDB lets the UI
 * render the previous values within ~16ms while the network request is
 * still in flight. This is the single largest "feels native" win for a
 * networked app: zero-latency perceived load on launch and on tab switch.
 *
 * Implementation notes:
 *  - We use idb-keyval (a tiny IndexedDB wrapper) so the cache survives
 *    process restarts on iOS/Android Capacitor builds and works on the web.
 *  - `staleTime` is set to 30s so persisted data is shown immediately and
 *    quietly revalidated in the background - no spinner flash.
 *  - `gcTime` (was cacheTime) is 24h so unused queries stick around long
 *    enough to be useful on next launch.
 *  - `maxAge` on the persister matches `gcTime` so we don't replay stale
 *    cache entries from weeks ago.
 *  - A `buster` derived from the build SHA invalidates the persisted cache
 *    on every deploy so old shapes never collide with new query selectors.
 */

import { QueryClient } from "@tanstack/react-query";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { get, set, del } from "idb-keyval";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: ONE_DAY_MS,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export const queryPersister = createAsyncStoragePersister({
  storage: {
    getItem: (key) => get<string>(key).then((v) => v ?? null),
    setItem: (key, value) => set(key, value),
    removeItem: (key) => del(key),
  },
  key: "elefa-rq-cache-v1",
  // Throttle writes so a noisy mutation burst doesn't pin the disk.
  throttleTime: 1000,
});

// Bump when a query selector / response shape changes app-wide.
// (Per-query invalidation should still use queryKey changes.)
export const QUERY_PERSIST_BUSTER =
  `${(import.meta.env.VITE_BUILD_SHA as string | undefined) ?? "local"}:elefa-option-a-app-api-v2-2026-07-05`;

export const QUERY_PERSIST_MAX_AGE = ONE_DAY_MS;
